import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";

// Types for the settings
interface CategorySetting {
  name: string;
  enabled: boolean;
}

interface SettingsContextType {
  settings: CategorySetting[];
  toggleEnabled: (index: number) => void;
  sortOrder: "newest" | "older";
  setSortOrder: (value: "newest" | "older") => void;
}

const initialSettings: CategorySetting[] = [
  { name: "Dining in", enabled: true },
  { name: "Takeaway", enabled: true },
  { name: "CounterSide", enabled: true },
  { name: "CurbSide", enabled: true },
  { name: "Online", enabled: true },
];

// Create the context
const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

// Provider component
export const SettingsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [settings, setSettings] = useState<CategorySetting[]>(initialSettings);
  const [sortOrder, setSortOrder] = useState<"newest" | "older">(() => {
    const saved = localStorage.getItem("kitchenSortOrder");
    return saved === "older" ? "older" : "newest";
  });

  const toggleEnabled = (index: number) => {
    setSettings((prev) =>
      prev.map((s, i) => (i === index ? { ...s, enabled: !s.enabled } : s))
    );
  };

  useEffect(() => {
    localStorage.setItem("kitchenSortOrder", sortOrder);
  }, [sortOrder]);

  return (
    <SettingsContext.Provider
      value={{ settings, toggleEnabled, sortOrder, setSortOrder }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook to use the context
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};
